import {PhoneHQGlyph} from 'ch3-ui-lib';
import { useAuthenticatedMedia } from "../../hooks/useAuthenticatedMedia";
import type { MatrixEncryptedFile } from "../../utils/encryptedMedia";

interface AuthenticatedImageProps {
    src: string;
    alt: string;
    className?: string;
    style?: React.CSSProperties;
    onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
    loading?: 'lazy' | 'eager';
    onLoad?: () => void;
    width?: number;
    height?: number;
    encryptedFile?: MatrixEncryptedFile;
    mimeType?: string;
    thumbnailSrc?: string;
    thumbnailEncryptedFile?: MatrixEncryptedFile;
    thumbnailWidth?: number;
    thumbnailHeight?: number;
}

export const AuthenticatedImage = ({
                                       src,
                                       alt,
                                       className,
                                       style,
                                       onClick,
                                       loading = 'lazy',
                                        onLoad,
                                        width,
                                        height,
                                        encryptedFile,
                                        mimeType,
                                        thumbnailSrc,
                                        thumbnailEncryptedFile,
                                        thumbnailWidth,
                                        thumbnailHeight,
                                    }: AuthenticatedImageProps) => {
    const { blobUrl, isLoading, error } = useAuthenticatedMedia(src, encryptedFile, mimeType);
    const shouldLoadThumbnail = isLoading && Boolean(thumbnailSrc);
    const { blobUrl: thumbnailBlobUrl } = useAuthenticatedMedia(
        shouldLoadThumbnail ? (thumbnailSrc ?? '') : '',
        shouldLoadThumbnail ? thumbnailEncryptedFile : undefined,
        'image/jpeg'
    );

    const placeholderWidth = width ?? thumbnailWidth;
    const placeholderHeight = height ?? thumbnailHeight;
    const placeholderStyle: React.CSSProperties = placeholderWidth && placeholderHeight
        ? { aspectRatio: `${placeholderWidth} / ${placeholderHeight}`, maxHeight: '300px', ...style }
        : { height: '150px', ...style };

    if (isLoading) {
        if (thumbnailBlobUrl) {
            return (
                <div style={{ position: 'relative' }}>
                    <img
                        src={thumbnailBlobUrl}
                        alt={alt}
                        className={className}
                        style={style}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                        <PhoneHQGlyph name="refresh" size={24} className="animate-spin text-white"/>
                    </div>
                </div>
            );
        }

        return (
            <div className={`flex items-center justify-center bg-surface-muted ${className}`} style={placeholderStyle}>
                <PhoneHQGlyph name="refresh" size={24} className="animate-spin text-ink-muted"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`flex items-center justify-center bg-surface-muted ${className}`} style={placeholderStyle}>
                <PhoneHQGlyph name="broken_image" size={24} className="text-ink-muted"/>
            </div>
        );
    }

    return (
        <img
            data-testid="chat.image.full"
            src={blobUrl}
            alt={alt}
            className={className}
            style={style}
            onClick={onClick}
            loading={loading}
            onLoad={onLoad}
        />
    );
};
